import { PlusIcon } from "@phosphor-icons/react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { ErrorOverlay } from "@/components/discovery/table-overlays/ErrorOverlay";
import NewsAlertsEmptyView from "@/components/news-alerts/NewsAlertsEmptyView";
import { DropdownButton } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import { Menu, MenuItem, MenuPopover, MenuTrigger } from "@/components/ui/menu";
import { agGridTheme } from "@/lib/ag-grid-theme";
import { openCreateNewsAlertModal } from "@/store/news-alerts/news-alerts.actions";
import type { TableAlert } from "@/store/news-alerts/news-alerts.slice";
import useScheduledBriefingStore from "@/store/news-alerts/scheduled-briefing/scheduled-briefing.slice";
import NewsAlertsTableActions from "./NewsAlertsTableActions";
import { useSchedulerTableItems } from "./useSchedulerTableItems";

const skeletonData = Array.from({ length: 5 }, () => ({}) as TableAlert);

const NewsAlertsList = () => {
  const gridRef = useRef<AgGridReact>(null);

  const { rows, fetching, error, refetch } = useSchedulerTableItems();
  const setCreateScheduledBriefingModalOpen = useScheduledBriefingStore(
    (state) => state.setCreateScheduledBriefingModalOpen,
  );

  const SkeletonCell = () => (
    <div className="flex items-center justify-center h-full">
      <Skeleton className={"h-4 w-full"} />
    </div>
  );

  const columnDefs: ColDef<TableAlert>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        width: 348,
        minWidth: 228,
        flex: 1,
        resizable: true,
        pinned: "left",
        lockPosition: true,
        cellRendererSelector: () =>
          fetching ? { component: SkeletonCell } : undefined,
      },
      {
        field: "schedule",
        headerName: "Schedule",
        width: 348,
        flex: 1,
        minWidth: 228,
        resizable: true,
        lockPosition: true,
        cellRendererSelector: () =>
          fetching ? { component: SkeletonCell } : undefined,
      },
      {
        field: "recipients",
        headerName: "Recipients",
        width: 120,
        minWidth: 100,
        resizable: true,
        lockPosition: true,
        cellRendererSelector: () =>
          fetching ? { component: SkeletonCell } : undefined,
      },
      {
        field: "lastUpdated",
        headerName: "Last Updated",
        width: 328,
        minWidth: 228,
        resizable: true,
        lockPosition: true,
        cellRendererSelector: () =>
          fetching ? { component: SkeletonCell } : undefined,
      },
      {
        field: "createdBy",
        headerName: "Created By",
        minWidth: 140,
        resizable: true,
        lockPosition: true,
        cellRendererSelector: () =>
          fetching ? { component: SkeletonCell } : undefined,
      },
      {
        width: 128,
        minWidth: 128,
        resizable: false,
        lockPosition: true,
        sortable: false,
        pinned: "right",
        cellRenderer: (params: ICellRendererParams<TableAlert>) => (
          <NewsAlertsTableActions params={params} isFetching={fetching} />
        ),
      },
    ],
    [fetching],
  );

  const rowData = useMemo(() => {
    if (fetching) return skeletonData;
    return rows;
  }, [fetching, rows]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const showErrorState = !fetching && !!error;
  const showEmptyState = !fetching && !error && rows.length === 0;

  useEffect(() => {
    if (!gridRef.current?.api) return;

    if (showErrorState) {
      gridRef.current.api.showNoRowsOverlay();
    } else {
      gridRef.current.api.hideOverlay();
    }
  }, [showErrorState]);

  const ErrorOverlayComponent = useMemo(
    () => () => (
      <ErrorOverlay title="Failed to Load News Alerts" onRetry={handleRetry} />
    ),
    [handleRetry],
  );

  return (
    <div className="flex flex-col p-2 gap-4">
      <div className="mb-4 p-2 flex justify-end">
        <MenuTrigger>
          <DropdownButton
            ariaLabel="Create options"
            label="Create"
            customLabel={<span className="text-xs font-medium">Create</span>}
            dropdownVariant="secondary"
            icon={<PlusIcon className="text-gray-600" />}
            showDivider
          />
          <MenuPopover placement="bottom end">
            <Menu>
              <MenuItem
                className="text-xs"
                onAction={() => {
                  openCreateNewsAlertModal();
                }}
              >
                Create news alert
              </MenuItem>
              <MenuItem
                className="text-xs"
                onAction={() => setCreateScheduledBriefingModalOpen(true)}
              >
                Create scheduled briefing
              </MenuItem>
            </Menu>
          </MenuPopover>
        </MenuTrigger>
      </div>
      {showEmptyState ? (
        <NewsAlertsEmptyView />
      ) : (
        <div className="w-full h-full">
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            theme={agGridTheme}
            enableCellTextSelection
            suppressDragLeaveHidesColumns
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={20}
            paginationPageSizeSelector={[10, 20, 50, 100]}
            loadingOverlayComponent={() => null}
            noRowsOverlayComponent={ErrorOverlayComponent}
            suppressServerSideFullWidthLoadingRow
          />
        </div>
      )}
    </div>
  );
};

export default NewsAlertsList;
